// Import the functions you need from the SDKs you need
import { initializeApp } from "firebase/app";
import { getFirestore } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { getStorage } from "firebase/storage";
import { initializeAnalytics, isSupported } from "firebase/analytics";

// Check for required environment variables
const requiredEnvVars = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID
};

// Check if all required variables are present
const missingVars = Object.entries(requiredEnvVars)
  .filter(([key, value]) => !value)
  .map(([key]) => key);

if (missingVars.length > 0) {
  console.error('❌ Firebase configuration error: Missing required environment variables:', missingVars);
  console.error('📝 Please create a .env file with your Firebase configuration. See README.md for setup instructions.');
  
  // In development, show a helpful error message
  if (import.meta.env.DEV) {
    const errorDiv = document.createElement('div');
    errorDiv.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      background: #ff4444;
      color: white;
      padding: 15px;
      text-align: center;
      z-index: 10000;
      font-family: Arial, sans-serif;
    `;
    errorDiv.innerHTML = `
      <strong>Firebase Configuration Error</strong><br>
      Missing environment variables: ${missingVars.join(', ')}<br>
      Please check your .env file and restart the development server.
    `;
    document.body.appendChild(errorDiv);
  }
}

// Your web app's Firebase configuration
const firebaseConfig = {
  apiKey: requiredEnvVars.apiKey,
  authDomain: requiredEnvVars.authDomain,
  projectId: requiredEnvVars.projectId,
  storageBucket: requiredEnvVars.storageBucket,
  messagingSenderId: requiredEnvVars.messagingSenderId,
  appId: requiredEnvVars.appId,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || undefined
};

// Only initialize Firebase if we have the required configuration
let app = null;
let db = null;
let auth = null;
let storage = null;
let analytics = null;

if (missingVars.length === 0) {
  try {
    // Initialize Firebase
    app = initializeApp(firebaseConfig);
    
    // Initialize Firebase services
    db = getFirestore(app);
    auth = getAuth(app);
    storage = getStorage(app);
    
    console.log('✅ Firebase initialized successfully');
    
    // Initialize Analytics with support check and error handling
    if (firebaseConfig.measurementId) {
      isSupported().then(supported => {
        if (supported) {
          try {
            // gtag in HTML sends the initial page_view; disable Firebase's duplicate
            analytics = initializeAnalytics(app, {
              config: { send_page_view: false }
            });
            console.log('✅ Firebase Analytics initialized successfully');
          } catch (error) {
            console.warn('⚠️ Firebase Analytics initialization failed:', error);
          }
        } else {
          console.log('ℹ️ Firebase Analytics not supported in this environment');
        }
      }).catch(err => {
        console.warn('⚠️ Firebase Analytics initialization failed:', err);
      });
    } else {
      console.log('ℹ️ Firebase Analytics disabled - no measurement ID provided');
    }
    
  } catch (error) {
    console.error('❌ Firebase initialization failed:', error);
  }
} else {
  console.error('❌ Firebase not initialized due to missing configuration');
}

// Expose app on window for admin tools (e.g. calling grantLifetimeAccessFromOrder from console)
if (typeof window !== 'undefined' && app) {
    window.__firebaseApp = app;
}

// Export everything needed
export { 
    app,
    db, 
    auth, 
    storage,
    analytics
}; 